import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppConstants } from '../constants';
import { ICanCancelResponse } from '../../model/cancellation/can-cancel-response.dto';
import { IPolicyCancelResponse } from '../../model/cancellation/policy-cancel-response.dto';
import { CancelConfirmInfo } from '../../model/cancellation/cancel-confirm-info';
import { PlanDetail } from '../../model/plan/plan-detail';
import { StorageService } from './storage.service';

interface ICancelPlanStateSnapshot {
  planNumber: string | null;
  certificateNumber: string | null;
  dealerCode: string | null;
  planDetail: PlanDetail | null;
  canCancel: ICanCancelResponse | null;
  confirmInfo: CancelConfirmInfo | null;
  cancelResult: IPolicyCancelResponse | null;
  cancelError: boolean;
  returnRoute: string;
}

/**
 * Shares state across the cancel-plan flow steps (ConfirmInfo -> Review ->
 * Result) without relying on fragile router `state` (which doesn't survive a
 * refresh and isn't available when a step is reached via direct navigation,
 * e.g. the standalone `/cancellation/review` route). `providedIn: 'root'` so
 * the my-plan entry point, the flow container, and each step share one
 * instance for the duration of the SPA session; `reset()` clears it once a
 * cancellation completes (or is abandoned) so a later attempt starts clean.
 *
 * Every mutation is also mirrored to `sessionStorage` (StorageService) and
 * rehydrated in the constructor, so a hard refresh on the Result step (a
 * customer wanting to print/save their cancellation confirmation) still finds
 * `planNumber` + `cancelResult` populated instead of bouncing back to my-plan
 * via `CancelPlanFlowComponent`'s "no entry state" guard.
 */
@Injectable({
  providedIn: 'root',
})
export class CancellationStateService {
  private storageService = inject(StorageService);

  $planNumber = new BehaviorSubject<string | null>(null);
  $certificateNumber = new BehaviorSubject<string | null>(null);
  $dealerCode = new BehaviorSubject<string | null>(null);
  /** The specific plan's full detail (name, dates, customers, claims) — the flow's own display ground truth, independent of whatever LoggedInUserSettings.$selectedPlanDetail happens to hold. */
  $planDetail = new BehaviorSubject<PlanDetail | null>(null);
  $canCancel = new BehaviorSubject<ICanCancelResponse | null>(null);
  $confirmInfo = new BehaviorSubject<CancelConfirmInfo | null>(null);
  $cancelResult = new BehaviorSubject<IPolicyCancelResponse | null>(null);
  $cancelError = new BehaviorSubject<boolean>(false);
  $returnRoute = new BehaviorSubject<string>('/my-plan');

  constructor() {
    const snapshot = this.storageService.getSession<ICancelPlanStateSnapshot>(AppConstants.STORAGE_KEYS.CancelPlanState);
    if (snapshot?.planNumber) {
      this.$planNumber.next(snapshot.planNumber);
      this.$certificateNumber.next(snapshot.certificateNumber);
      this.$dealerCode.next(snapshot.dealerCode);
      this.$planDetail.next(snapshot.planDetail);
      this.$canCancel.next(snapshot.canCancel);
      this.$confirmInfo.next(snapshot.confirmInfo);
      this.$cancelResult.next(snapshot.cancelResult);
      this.$cancelError.next(snapshot.cancelError);
      this.$returnRoute.next(snapshot.returnRoute);
    }
  }

  private persist(): void {
    this.storageService.setSession<ICancelPlanStateSnapshot>(AppConstants.STORAGE_KEYS.CancelPlanState, {
      planNumber: this.planNumber,
      certificateNumber: this.certificateNumber,
      dealerCode: this.dealerCode,
      planDetail: this.planDetail,
      canCancel: this.canCancel,
      confirmInfo: this.confirmInfo,
      cancelResult: this.cancelResult,
      cancelError: this.cancelError,
      returnRoute: this.returnRoute,
    });
  }

  get planNumber(): string | null {
    return this.$planNumber.value;
  }

  get certificateNumber(): string | null {
    return this.$certificateNumber.value;
  }

  get dealerCode(): string | null {
    return this.$dealerCode.value;
  }

  get planDetail(): PlanDetail | null {
    return this.$planDetail.value;
  }

  get canCancel(): ICanCancelResponse | null {
    return this.$canCancel.value;
  }

  get confirmInfo(): CancelConfirmInfo | null {
    return this.$confirmInfo.value;
  }

  get cancelResult(): IPolicyCancelResponse | null {
    return this.$cancelResult.value;
  }

  get cancelError(): boolean {
    return this.$cancelError.value;
  }

  /** Route the Result step's "Return to plan" button navigates back to — the page the flow was entered from. */
  get returnRoute(): string {
    return this.$returnRoute.value;
  }

  /**
   * Called by the my-plan / plan-coverage entry point once canCancel() resolves.
   * `planDetail` is the clicked plan's own full detail — required on a multi-plan
   * list (e.g. plan-coverage-single-cert-policy) where `LoggedInUserSettings.$selectedPlanDetail`
   * may point at a different plan than the one the customer just clicked "Cancel" on.
   */
  startFlow(planNumber: string, certificateNumber: string, dealerCode: string, canCancel: ICanCancelResponse, returnRoute = '/my-plan', planDetail: PlanDetail | null = null): void {
    this.reset();
    this.$planNumber.next(planNumber);
    this.$certificateNumber.next(certificateNumber);
    this.$dealerCode.next(dealerCode);
    this.$planDetail.next(planDetail);
    this.$canCancel.next(canCancel);
    this.$returnRoute.next(returnRoute);
    this.persist();
  }

  /** Patches in a freshly-refetched detail for the flow's own plan (e.g. after canCancel() reports false, or on Result's open-claim check). */
  setPlanDetail(planDetail: PlanDetail | null): void {
    this.$planDetail.next(planDetail);
    this.persist();
  }

  setConfirmInfo(info: CancelConfirmInfo): void {
    this.$confirmInfo.next(info);
    this.persist();
  }

  setCancelResult(result: IPolicyCancelResponse): void {
    this.$cancelError.next(false);
    this.$cancelResult.next(result);
    this.persist();
  }

  setCancelError(): void {
    this.$cancelResult.next(null);
    this.$cancelError.next(true);
    this.persist();
  }

  reset(): void {
    this.$planNumber.next(null);
    this.$certificateNumber.next(null);
    this.$dealerCode.next(null);
    this.$planDetail.next(null);
    this.$canCancel.next(null);
    this.$confirmInfo.next(null);
    this.$cancelResult.next(null);
    this.$cancelError.next(false);
    this.$returnRoute.next('/my-plan');
    this.storageService.setSession(AppConstants.STORAGE_KEYS.CancelPlanState, null);
  }
}
